import type { AclsACLSchemaNullable } from '@iconik-io/iconik-api/src/services/acls';

import type { PermissionValues } from '../../types/common';
import {
  type ACL_TYPE_INHERITED,
  type ACL_TYPE_OWN,
  type ACL_TYPE_PROPAGATED,
  aclPrefixes,
} from './constants';

export type CommonAclsPrefix = (typeof aclPrefixes)[number];

const aclPrefixSet: ReadonlySet<string> = new Set(aclPrefixes);

export const isCommonAclsPrefix = (value: string): value is CommonAclsPrefix =>
  aclPrefixSet.has(value);

export type AclType =
  | typeof ACL_TYPE_OWN
  | typeof ACL_TYPE_INHERITED
  | typeof ACL_TYPE_PROPAGATED;

export type SetPayload = {
  id: string;
  acl: AclsACLSchemaNullable;
};

export type SetCurrentPayload = {
  id: string;
  permissions: PermissionValues;
};

export type AddToUserPayload = {
  id: string;
  user_id: string;
  permissions: string[];
  type: AclType;
  object_type?: string;
  object_key?: string;
};

export type AddToGroupPayload = {
  id: string;
  group_id: string;
  permissions: string[];
  type: AclType;
  object_type?: string;
  object_key?: string;
};

export type DeleteFromUserPayload = {
  id: string;
  user_id: string;
  type: AclType;
};

export type DeleteFromGroupPayload = {
  id: string;
  group_id: string;
  type: AclType;
};

export type AccessGrant = {
  user_ids?: string[];
  group_ids?: string[];
  permissions: string[];
};

export type EntityUserAcl = {
  user_id: string;
  permissions: string[];
  type: AclType;
  objectType?: string;
  objectKey?: string;
};

export type EntityGroupAcl = {
  group_id: string;
  permissions: string[];
  type: AclType;
  objectType?: string;
  objectKey?: string;
};

type EntityAcl = EntityUserAcl | EntityGroupAcl;

export const isGroupAcl = (acl: EntityAcl): acl is EntityGroupAcl =>
  Object.hasOwn(acl, 'group_id');

export const isUserAcl = (acl: EntityAcl): acl is EntityUserAcl =>
  Object.hasOwn(acl, 'user_id');

export type EntityUsersAclsRecord = {
  [objectId: string]: EntityUserAcl[];
};

export type EntityGroupsAclsRecord = {
  [objectId: string]: EntityGroupAcl[];
};

export type CommonAclsAppState = {
  entityUsersAcls: EntityUsersAclsRecord;
  entityGroupsAcls: EntityGroupsAclsRecord;
  entityCurrentUserAcls: {
    [objectId: string]: PermissionValues;
  };
  edit?: string;
  confirmDelete?: string;
  confirmApply?: string;
};
